import { Component, Input, Output, EventEmitter, OnDestroy, HostListener } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';
import { AuthService, AuthUser } from '../../services/auth.service';
import { TranslationService } from '../../services/translation.service';
import type { HeaderTranslations } from '../../i18n/header';
import en from '../../i18n/en/header';
import {
  SuperDropdownComponent,
  DropdownItem,
} from '../shared/super-dropdown/super-dropdown.component';
import { Subscription, catchError, filter, of } from 'rxjs';
import { UserCredits, UserService } from '../../services/user.service';
import { EntitlementService } from '../../core/services/entitlement.service';
import { PlansService } from '../../services/plans.service';
import type { ApplicationsBalance, Entitlements } from '../../shared/models/entitlements';
import { CreditsRingComponent } from '../shared/credits-ring/credits-ring.component';
import { NotificationBellComponent } from '../notification-bell/notification-bell.component';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, SuperDropdownComponent, CreditsRingComponent, NotificationBellComponent],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  animations: [
    trigger('fade', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('150ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' }))
      ])
    ])
  ]
})
export class HeaderComponent {
  @Output() menuToggle = new EventEmitter<void>();
  /** chamba-ai#433: drives the hamburger's aria-expanded / label. */
  @Input() navExpanded = true;

  dropdownItems: DropdownItem[] = [];
  languageDropdownItems: DropdownItem[] = [];
  isAdmin = false;

  user: AuthUser | null = null;
  translations: HeaderTranslations = en;
  currentLanguage = 'en';
  languageDropdownOpen = false;
  /** chamba-ai#433: AI credits ring in the navbar. */
  credits: UserCredits | null = null;
  /** #384 Phase 7: per-cycle application balance + real plan (launch plans, Pioneer). */
  entitlements: Entitlements | null = null;
  private planNames = new Map<string, string>();
  /** chamba-ai#411: notification bell, gated per environment. */
  readonly bellEnabled = environment.enableNotificationBell;
  private subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private router: Router,
    private translationService: TranslationService,
    private userService: UserService,
    private entitlementService: EntitlementService,
    private plansService: PlansService
  ) {
    this.currentLanguage = this.translationService.getCurrentLanguage();
    this.buildLanguageDropdownItems();
    
    this.subscription.add(this.authService.refreshUser().subscribe());

    // Credits change as AI features run, so re-read them after each
    // navigation (a single small GET) instead of only once per session.
    this.loadCredits();
    this.subscription.add(this.entitlementService.entitlements$.subscribe((ent) => (this.entitlements = ent)));
    this.subscription.add(
      this.plansService.getPlans().pipe(catchError(() => of([]))).subscribe((plans) => {
        this.planNames = new Map(plans.map((p) => [p.id, p.name]));
      })
    );
    this.refreshEntitlements();
    this.subscription.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => {
          this.loadCredits();
          this.refreshEntitlements();
        })
    );
    this.subscription.add(
      this.authService.currentUser$.subscribe((u) => {
        this.user = u;
        this.isAdmin = this.authService.isAdmin;
        this.buildDropdownItems();
      })
    );

    const current = this.translationService.getHeaderTranslations();
    if (current) {
      this.translations = current;
    }
    this.translationService.onChange(() => {
      const next = this.translationService.getHeaderTranslations();
      if (next) {
        this.translations = next;
      }
      this.buildDropdownItems();
      this.buildLanguageDropdownItems();
    });
  }

  get t(): HeaderTranslations {
    return this.translations;
  }

  get creditsLabel(): string {
    const balance = this.applicationsBalance;
    if (balance) return this.t.APPLICATIONS_LEFT.replace('{count}', String(balance.available));
    if (!this.credits) return this.t.AI_CREDITS;
    if (this.credits.total === null) return this.t.AI_CREDITS_UNLIMITED;
    return this.t.AI_CREDITS_LEFT.replace('{percent}', String(this.creditsPercentLeft));
  }

  /** Share of the period's AI credits still available, 0-100. A
   * percentage, not the raw count - plans are metered in tokens, and
   * "1,240,000 left" reads oddly in a navbar. */
  get creditsPercentLeft(): number {
    if (!this.credits || !this.credits.total) return 0;
    const ratio = (this.credits.remaining ?? 0) / this.credits.total;
    return Math.round(Math.min(1, Math.max(0, ratio)) * 100);
  }

  /** Compact label next to the ring, e.g. "92% left" / "Unlimited". */
  get creditsShortLabel(): string {
    const balance = this.applicationsBalance;
    if (balance) return this.t.APPLICATIONS_LEFT_SHORT.replace('{count}', String(balance.available));
    if (!this.credits || this.credits.total === null) return this.t.AI_CREDITS_UNLIMITED_SHORT;
    return this.t.AI_CREDITS_LEFT_SHORT.replace('{percent}', String(this.creditsPercentLeft));
  }

  private loadCredits(): void {
    this.subscription.add(
      this.userService
        .getMyCredits()
        .pipe(catchError(() => of(null)))
        .subscribe((credits) => (this.credits = credits))
    );
  }

  private refreshEntitlements(): void {
    this.subscription.add(this.entitlementService.load().pipe(catchError(() => of(void 0))).subscribe());
  }

  /** Applications balance for plans on the per-cycle quota (launch plans and
   * Pioneer); null for legacy plans, which keep the AI-credits ring. */
  get applicationsBalance(): ApplicationsBalance | null {
    return this.entitlements?.applications ?? null;
  }

  get applicationsTotal(): number {
    const b = this.applicationsBalance;
    if (!b) return 0;
    // quota + every active grant's size, so the ring reads "left of what you had".
    return b.quota + b.grants.reduce((sum, g) => sum + g.amount, 0);
  }

  get hasPaidPlan(): boolean {
    const plan = (this.user?.subscription_plan || '').toLowerCase();
    const status = (this.user?.subscription_status || '').toLowerCase();
    return !!plan && plan !== 'free' && (status === 'active' || status === 'trialing');
  }

  get isPioneerActive(): boolean {
    return this.entitlements?.plan === 'pioneer';
  }

  get isSubscriptionPending(): boolean {
    const status = (this.user?.subscription_status || '').toLowerCase();
    return status === 'incomplete' || status === 'past_due' || status === 'unpaid';
  }

  /** Short plan name for the account chip; null when the user has no plan. */
  get planTagLabel(): string | null {
    if (this.isPioneerActive) return this.t.PLAN_BADGE_PIONEER;
    if (!this.hasPaidPlan) return null;
    const plan = (this.user?.subscription_plan || '').toLowerCase();
    return this.planNames.get(plan) ?? (plan === 'pro' ? this.t.PLAN_BADGE_PRO : plan);
  }

  /** The "choose a plan" call to action: for anyone without an active paid
   * plan (including Pioneer users, whose free window ends). */
  get showPlanCta(): boolean {
    return !this.hasPaidPlan;
  }

  get planCtaLabel(): string {
    return this.isSubscriptionPending ? this.t.SUBSCRIPTION_PENDING : this.t.CHOOSE_PLAN;
  }

  private buildDropdownItems(): void {
    this.dropdownItems = [
      { label: this.t.PROFILE, icon: 'person', action: 'profile' },
      { label: this.t.PLANS, icon: 'workspace_premium', action: 'plans' },
      { label: this.t.SETTINGS, icon: 'settings', action: 'settings' },
    ];

    if (this.isAdmin) {
      this.dropdownItems.push({ divider: true });
      this.dropdownItems.push({
        label: this.t.ADMIN_PANEL,
        icon: 'admin_panel_settings',
        action: 'admin',
      });
    }

    // this.dropdownItems.push({ divider: true });
    // this.dropdownItems.push({ label: 'Navigator', icon: 'menu', action: 'left-navigation-panel' });
    this.dropdownItems.push({ divider: true });
    this.dropdownItems.push({ label: this.t.LOGOUT, icon: 'logout', action: 'logout' });
  }

  private buildLanguageDropdownItems(): void {
    this.languageDropdownItems = [
      { 
        label: this.t.ENGLISH, 
        action: 'lang-en',
        icon: this.currentLanguage === 'en' ? 'check' : ''
      },
      { 
        label: this.t.GERMAN, 
        action: 'lang-de',
        icon: this.currentLanguage === 'de' ? 'check' : ''
      },
      { 
        label: this.t.SPANISH, 
        action: 'lang-es',
        icon: this.currentLanguage === 'es' ? 'check' : ''
      },
      {
        label: this.t.PORTUGUESE,
        action: 'lang-pt',
        icon: this.currentLanguage === 'pt' ? 'check' : ''
      }
    ];
  }

  handleDropdownAction(action: string) {
    // Handle language switching
    if (action.startsWith('lang-')) {
      const lang = action.replace('lang-', '');
      this.switchLanguage(lang);
      return;
    }
    
    // Implement your action logic here (e.g., route or logout)
    if (action === 'left-navigation-panel') {
      this.menuToggle.emit();
      return;
    }
    if (action === 'plans') {
      this.router.navigateByUrl('/home/plans');
      return;
    }
    if (action === 'profile') {
      this.router.navigateByUrl('/home/profile');
      return;
    }
    if (action === 'settings') {
      this.router.navigateByUrl('/home/settings');
      return;
    }
    if (action === 'logout') {
      this.authService.logout();
      this.router.navigateByUrl('/login');
      return;
    }
    if (action === 'admin') {
      this.router.navigateByUrl('/admin');
      return;
    }
  }

  private async switchLanguage(lang: string) {
    if (lang !== this.currentLanguage) {
      this.currentLanguage = lang;
      await this.translationService.setLanguage(lang);
      this.buildLanguageDropdownItems();
    }
    this.languageDropdownOpen = false;
  }

  goToPlans(): void {
    this.router.navigateByUrl('/home/plans');
  }

  goHome(): void {
    // chamba-ai#433: same target as the breadcrumb's "Home" crumb.
    this.router.navigateByUrl('/home/dashboard');
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  toggleLanguageDropdown(): void {
    this.languageDropdownOpen = !this.languageDropdownOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const languageButton = target.closest('.language-menu');
    const languageDropdown = target.closest('.language-dropdown');
    
    if (!languageButton && !languageDropdown) {
      this.languageDropdownOpen = false;
    }
  }
}
